import { useEffect, useRef } from "react";

import BrushWord from "../components/BrushWord";
import HexLattice from "../components/HexLattice";
import { HERO } from "../data/content";
import {
  gsap,
  isTouch,
  magnetic,
  parallax,
  prefersReducedMotion,
  splitText,
} from "../lib/motion";
import "./Hero.css";

/* Decorative shapes shipped by the reference site, kept in place. */
const OUTER_SHAPES = [
  { src: "hero-circle.png", cls: "s-circle", w: 56, h: 59 },
  { src: "hero-circle-2.png", cls: "s-circle-2", w: 55, h: 191 },
  { src: "hero-dot-2.png", cls: "s-dot-2", w: 47, h: 74 },
];

const INNER_SHAPES = [
  { src: "hero-dot.png", cls: "s-dot", w: 70, h: 110 },
  /* 316, not the reference's 330: at 330² the ornament is 2% larger than the
     hero photograph (400×267) and Chrome elects it the LCP element — a
     decorative blur that fades in late then defines the page's paint score.
     At 316² the photograph outranks it everywhere it's visible. */
  { src: "hero-circle-3.png", cls: "s-circle-3", w: 316, h: 316 },
  { src: "hero-circle-4.png", cls: "s-circle-4", w: 170, h: 170 },
];

export default function Hero({ ready }) {
  const rootRef = useRef(null);
  const titleRef = useRef(null);
  const quoteRef = useRef(null);
  const mainRef = useRef(null);
  const sideRef = useRef(null);

  /* --- Entrance: runs once the loader curtain has cleared ---------------- */
  useEffect(() => {
    if (!ready) return undefined;
    const root = rootRef.current;
    if (!root) return undefined;

    const cleanups = [];

    if (prefersReducedMotion()) {
      gsap.set(root.querySelectorAll("[data-anim]"), { opacity: 1, y: 0 });
      return () => {};
    }

    /* Split only the leaf text nodes, so BrushWord's stroke stays intact. */
    const leaves = titleRef.current.querySelectorAll("[data-split]");
    const splits = [...leaves].map((leaf) => splitText(leaf, "chars"));
    const chars = splits.flatMap((s) => s.units);

    /* Every step is a fromTo with both ends stated. See the note on revealUp:
       `from` infers its end state at creation, which StrictMode's second mount
       reads back as the faded-out state the first mount left behind. */
    const tl = gsap.timeline({ delay: 0.06 });

    tl.fromTo(
      chars,
      { yPercent: 118 },
      { yPercent: 0, duration: 0.7, ease: "power4.out", stagger: 0.02 },
    )
      .fromTo(
        root.querySelectorAll("[data-anim='fade']"),
        { y: 22, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.62, ease: "power3.out", stagger: 0.08 },
        0.18,
      )
      .fromTo(
        [mainRef.current, sideRef.current],
        { clipPath: "inset(0 0 100% 0)", scale: 1.14 },
        {
          clipPath: "inset(0 0 0% 0)",
          scale: 1,
          duration: 0.9,
          ease: "power3.out",
          stagger: 0.12,
        },
        0.14,
      )
      .fromTo(
        quoteRef.current,
        { y: 26, scale: 0.92, opacity: 0 },
        { y: 0, scale: 1, opacity: 1, duration: 0.52, ease: "back.out(1.7)" },
        0.78,
      )
      .fromTo(
        root.querySelectorAll(".ilab-hero__shape"),
        { opacity: 0, scale: 0.7 },
        { opacity: 1, scale: 1, duration: 0.7, ease: "power2.out", stagger: 0.05 },
        /* Early, so if a shape is ever elected LCP it paints with the hero,
           not seconds after it. */
        0.12,
      );

    cleanups.push(() => {
      tl.revert();
      splits.forEach((s) => s.restore());
    });

    /* --- Ambient drift on the decorative shapes ------------------------- */
    root.querySelectorAll(".ilab-hero__shape").forEach((shape, i) => {
      const drift = gsap.to(shape, {
        y: i % 2 ? 18 : -22,
        x: i % 3 ? -12 : 14,
        duration: 5 + i * 0.7,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        delay: 1.2 + i * 0.12,
      });
      cleanups.push(() => drift.kill());
    });

    /* The reference bounces this badge on a loop; kept, but slowed down. */
    const bob = gsap.to(quoteRef.current, {
      y: -9,
      duration: 2.4,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut",
      delay: 1.6,
    });
    cleanups.push(() => bob.kill());

    return () => cleanups.forEach((fn) => fn());
  }, [ready]);

  /* --- Scroll + pointer parallax ---------------------------------------- */
  useEffect(() => {
    /* `top top` so both photographs sit exactly where they are designed to at
       scroll 0 and only separate once the page actually moves. */
    const cleanups = [
      parallax(mainRef.current, { distance: -46, trigger: rootRef.current, start: "top top" }),
      parallax(sideRef.current, { distance: -92, trigger: rootRef.current, start: "top top" }),
      magnetic(quoteRef.current, { strength: 0.1 }),
    ];

    if (!isTouch() && !prefersReducedMotion()) {
      const root = rootRef.current;
      const layers = [
        { el: mainRef.current, depth: 10 },
        { el: sideRef.current, depth: 20 },
        ...[...root.querySelectorAll(".ilab-hero__shape")].map((el, i) => ({
          el,
          depth: 26 + i * 6,
        })),
      ];

      const setters = layers.map(({ el, depth }) => ({
        depth,
        x: gsap.quickTo(el, "xPercent", { duration: 1.1, ease: "power3.out" }),
        y: gsap.quickTo(el, "yPercent", { duration: 1.1, ease: "power3.out" }),
      }));

      const onMove = (event) => {
        const nx = event.clientX / window.innerWidth - 0.5;
        const ny = event.clientY / window.innerHeight - 0.5;
        setters.forEach(({ depth, x, y }) => {
          x((-nx * depth) / 10);
          y((-ny * depth) / 10);
        });
      };

      window.addEventListener("pointermove", onMove);
      cleanups.push(() => window.removeEventListener("pointermove", onMove));
    }

    return () => cleanups.forEach((fn) => fn());
  }, []);

  return (
    <section id="main" className="ilab-hero" ref={rootRef}>
      <HexLattice radius={74} />

      <div className="ilab-hero__shapes" aria-hidden="true">
        {OUTER_SHAPES.map(({ src, cls, w, h }) => (
          <img
            key={src}
            className={`ilab-hero__shape ${cls}`}
            src={`/assets/shape/${src}`}
            alt=""
            width={w}
            height={h}
          />
        ))}
      </div>

      <div className="container ilab-hero__inner">
        <div className="ilab-hero__copy">
          <p className="eyebrow" data-anim="fade">
            {HERO.eyebrow}
          </p>

          {/* Two lines with opposing weights — light lead, bold tail — the
              reference's own structure, driven there by a `span:first-child`
              rule that sets display:block and font-weight:300. */}
          <h1 className="ilab-hero__title" ref={titleRef}>
            <BrushWord variant="hero" delay={0.5}>
              <span data-split>{HERO.titleLead}</span>
            </BrushWord>
            <span className="ilab-hero__title-tail" data-split>
              {HERO.titleTail}
            </span>
          </h1>

          <p className="ilab-hero__lede" data-anim="fade">
            {HERO.lede}
          </p>
        </div>

        <div className="ilab-hero__media">
          <div className="ilab-hero__shapes ilab-hero__shapes--inner" aria-hidden="true">
            {INNER_SHAPES.map(({ src, cls, w, h }) => (
              <img
                key={src}
                className={`ilab-hero__shape ${cls}`}
                src={`/assets/shape/${src}`}
                alt=""
                width={w}
                height={h}
              />
            ))}
          </div>

          <figure className="ilab-hero__main">
            <picture>
              <source
                type="image/webp"
                srcSet="/assets/images/hero-main-400.webp 400w, /assets/images/hero-main-600.webp 600w, /assets/images/hero-main-800.webp 800w"
                sizes="(max-width: 767px) 92vw, 400px"
              />
              <img
                ref={mainRef}
                src="/assets/images/hero-main-600.jpg"
                alt="Two Intelligence Lab students programming a robot on a laptop"
                width="600"
                height="400"
                fetchPriority="high"
                decoding="async"
              />
            </picture>

            <figcaption className="ilab-hero__quote" ref={quoteRef}>
              <span>{HERO.quote.kicker}</span>
              <strong>{HERO.quote.title}</strong>
            </figcaption>
          </figure>

          <figure className="ilab-hero__side">
            <picture>
              <source
                type="image/webp"
                srcSet="/assets/images/hero-side-200.webp 200w, /assets/images/hero-side-300.webp 300w, /assets/images/hero-side-400.webp 400w"
                sizes="200px"
              />
              <img
                ref={sideRef}
                src="/assets/images/hero-side-300.jpg"
                alt="Three students watching an EV3 robot drive across a table"
                width="300"
                height="200"
                loading="lazy"
                decoding="async"
              />
            </picture>
          </figure>
        </div>
      </div>
    </section>
  );
}
